import React, { useState } from 'react';
import CSPSimulator from './CSPSimulatorV2';
import { V, serif, btnGold, btnGhost } from '../theme';

function getChapterCompletion() {
  const getChapterDone = (chId) => {
    // Try tlaw_chapters first
    try {
      const chapters = JSON.parse(localStorage.getItem('tlaw_chapters') || '{}');
      if (chapters?.[chId]?.completed || chapters?.[chId]?.quizPassed || chapters?.[chId]?.passed) {
        return true;
      }
    } catch(e) {}

    // Try individual chapter keys
    try {
      const key = localStorage.getItem(`tlaw_${chId}_complete`);
      if (key === 'true') return true;
    } catch(e) {}

    // Try progress key
    try {
      const progress = JSON.parse(localStorage.getItem('tlaw_progress') || '{}');
      if (progress?.[chId]?.completed || progress?.[chId]?.passed) return true;
    } catch(e) {}

    // Try foundation state completedChapters array
    try {
      const mainState = JSON.parse(localStorage.getItem('tlw_foundation_state') || '{}');
      if ((mainState.completedChapters || []).includes(chId)) return true;
    } catch(e) {}

    return false;
  };

  const ch06Done = getChapterDone('ch06');
  const ch07Done = getChapterDone('ch07');
  const ch08Done = getChapterDone('ch08');

  console.log('ch06Done:', ch06Done);
  console.log('ch07Done:', ch07Done);
  console.log('tlaw_chapters:', localStorage.getItem('tlaw_chapters'));

  return { ch06Done, ch07Done, ch08Done };
}

function PreUnlockScreen({ ch06Done, ch07Done, ch08Done, onNavigate }) {
  const cards = [
    {
      unlocked: ch06Done,
      badge: ch06Done ? '✓ Unlocked' : '🔒 Complete Chapter 6',
      badgeColor: ch06Done ? V.sage : V.rose,
      title: 'Put Strategies',
      items: ['Cash Secured Put', 'Bull Put Spread'],
      btn: ch06Done ? null : { label: 'Go to Chapter 6 →', onClick: () => onNavigate('chapter_ch06') },
    },
    {
      unlocked: ch07Done,
      badge: ch07Done ? '✓ Unlocked' : '🔒 Complete Chapter 7',
      badgeColor: ch07Done ? V.sage : V.muted,
      title: 'Call Strategies',
      items: ['Covered Call', 'Bear Call Spread'],
      btn: null,
    },
    {
      unlocked: ch08Done,
      badge: ch08Done ? '✓ Unlocked' : '🔒 Complete Chapter 8',
      badgeColor: ch08Done ? V.sage : V.muted,
      title: 'The Wheel',
      items: ['Full cycle management'],
      btn: null,
    },
  ];

  return (
    <div style={{ padding: '40px 24px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ ...serif, fontSize: '36px', fontWeight: 500, color: V.text, margin: 0 }}>
          The Full Trade Simulator
        </h1>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        {cards.map((c, i) => (
          <div key={i} style={{
            background: V.bg2,
            border: `0.5px solid ${V.border}`,
            borderRadius: '10px',
            padding: '20px',
            opacity: c.unlocked ? 1 : 0.85,
          }}>
            <div style={{
              fontSize: '11px',
              color: c.badgeColor,
              marginBottom: '12px',
              fontWeight: 500,
              fontFamily: "'Jost', sans-serif",
              letterSpacing: '0.04em',
            }}>
              {c.badge}
            </div>
            <div style={{ ...serif, fontSize: '18px', color: V.text, marginBottom: '10px' }}>
              {c.title}
            </div>
            {c.items.map((item, j) => (
              <div key={j} style={{
                fontSize: '13px',
                color: V.muted,
                lineHeight: 1.7,
                fontFamily: "'Jost', sans-serif",
              }}>
                {item}
              </div>
            ))}
            {c.btn && (
              <button style={{ ...btnGold, marginTop: '18px', width: '100%', padding: '10px 14px', fontSize: '12px' }}
                onClick={c.btn.onClick}>
                {c.btn.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ComingSoonCard({ strategy, onNavigate }) {
  const info = {
    cc:    { name: 'Covered Call',     chap: 'ch07', num: 7 },
    bcs:   { name: 'Bear Call Spread', chap: 'ch07', num: 7 },
    wheel: { name: 'The Wheel',        chap: 'ch08', num: 8 },
  };
  const { name, chap, num } = info[strategy] || { name: strategy, chap: 'ch07', num: 7 };

  return (
    <div style={{ padding: '40px 24px', display: 'flex', justifyContent: 'center' }}>
      <div style={{
        background: V.bg2,
        border: `1px solid ${V.gold}`,
        borderRadius: '10px',
        padding: '24px',
        maxWidth: '480px',
        width: '100%',
      }}>
        <div style={{ ...serif, fontSize: '22px', color: V.gold, marginBottom: '16px' }}>
          {name} — Coming Soon
        </div>
        <p style={{
          fontSize: '13px',
          color: V.muted,
          lineHeight: 1.8,
          marginBottom: '20px',
          fontFamily: "'Jost', sans-serif",
        }}>
          {name} simulator is being built now.
          <br /><br />
          You have unlocked this strategy by completing Chapter {num}.
          <br /><br />
          In the meantime practice the strategy concepts in Chapter {num} and return here soon.
        </p>
        <button style={{ ...btnGold }} onClick={() => onNavigate(`chapter_${chap}`)}>
          Review Chapter {num} →
        </button>
      </div>
    </div>
  );
}

export default function FullTradeSimulator({ onNavigate }) {
  const { ch06Done, ch07Done, ch08Done } = getChapterCompletion();
  const [strategy, setStrategy] = useState('csp');

  if (!ch06Done) {
    return (
      <PreUnlockScreen
        ch06Done={ch06Done}
        ch07Done={ch07Done}
        ch08Done={ch08Done}
        onNavigate={onNavigate}
      />
    );
  }

  const tabs = [
    { key: 'csp',   label: 'Cash Secured Put',  show: true },
    { key: 'bps',   label: 'Bull Put Spread',    show: true },
    { key: 'cc',    label: 'Covered Call',        show: ch07Done },
    { key: 'bcs',   label: 'Bear Call Spread',    show: ch07Done },
    { key: 'wheel', label: 'The Wheel',           show: ch08Done },
  ].filter(t => t.show);

  const isPut   = strategy === 'csp' || strategy === 'bps';
  const isCall  = strategy === 'cc'  || strategy === 'bcs';
  const isWheel = strategy === 'wheel';

  return (
    <div>
      <div style={{ padding: '32px 24px 0', maxWidth: '960px', margin: '0 auto' }}>
        <h1 style={{ ...serif, fontSize: '32px', fontWeight: 500, color: V.text, marginBottom: '6px' }}>
          The Full Trade Simulator
        </h1>
        <p style={{
          fontSize: '13px',
          color: V.muted,
          marginBottom: '24px',
          fontFamily: "'Jost', sans-serif",
        }}>
          Build market conditions. Choose your strategy. Trade it.
        </p>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '28px' }}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setStrategy(t.key)}
              style={{
                padding: '10px 20px',
                background: strategy === t.key ? 'rgba(201,168,76,0.15)' : 'transparent',
                border: `0.5px solid ${strategy === t.key ? 'rgba(201,168,76,0.6)' : V.border}`,
                borderRadius: '8px',
                color: strategy === t.key ? '#d4b86a' : V.muted,
                fontSize: '13px',
                fontWeight: strategy === t.key ? 500 : 400,
                cursor: 'pointer',
                fontFamily: "'Jost', sans-serif",
                transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {isPut   && <CSPSimulator onNavigate={onNavigate} strategyType="put" hideHeader={true} />}
      {isCall  && <ComingSoonCard strategy={strategy} onNavigate={onNavigate} />}
      {isWheel && <ComingSoonCard strategy={strategy} onNavigate={onNavigate} />}
    </div>
  );
}
